import { useEffect, useRef, useState } from 'react';
import { Modal } from '../../components/ui';
import { api } from '../../lib/api';
import charterHtml from './cooCharter.html?raw';
import { collectCharter, renderCharterReport } from './charterReport';

/**
 * The COO Success Charter, as the standalone EOS app presents it.
 *
 * Brought across from `C:\Development\EOS` (`frontend/src/pages/COOCharter.jsx`
 * plus `cooCharter.html`, copied byte for byte — except its script, which now
 * lives in `web/public/coo-charter/charter.js`; see srcDoc) by request. The charter is a
 * self-contained, richly-styled document — its own theme, its own canvas
 * background, its own autosave — so it is hosted in an ISOLATED IFRAME, which
 * is what the source app does and for the same reason: its global CSS and its
 * top-level script would otherwise land on the whole app. srcDoc keeps the
 * frame on this origin, so the localStorage the document reads and writes is
 * the same localStorage this component seeds.
 *
 * TWO LAYERS OF SAVING, both the source app's:
 *   - the document autosaves every note and checkbox to localStorage, per
 *     browser, 350ms after you stop typing. That is what makes it feel like a
 *     document rather than a form.
 *   - "Save Charter" lifts that state to the organisation's copy, so a charter
 *     filled in on one machine opens filled in on another, and appends a
 *     restorable snapshot.
 *
 * On save we read the values straight out of the iframe's DOM rather than out
 * of localStorage, so a save within 350ms of the last keystroke still catches
 * it. On open we seed localStorage BEFORE mounting the frame, because the
 * document reads it once on init.
 *
 * THREE THINGS COULD NOT COME ACROSS LITERALLY, all for the same reason as the
 * ownership map: the source is a Tailwind app with lucide-react icons and this
 * one is neither. The toolbar is built from this app's own button and modal
 * components, and the five glyphs are text. The document itself — every word,
 * every rule, every colour inside the frame — is untouched.
 */

/** Must match the store PREFIX inside public/coo-charter/charter.js. */
const PREFIX = 'dtc-coo-v1-';

type CharterState = Record<string, string>;

type Version = {
  VersionId: number;
  SavedUtc: string;
  SavedBy: string | null;
  state: CharterState;
};

type SavedDoc = { state: CharterState | null; updatedUtc: string | null; updatedBy: string | null };

const fmtSaved = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const fmtVersion = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
};

/** Read the charter's live note/checkbox values out of the (same-origin) frame.
 *  Falls back to whatever the document last wrote to localStorage. */
function readCharterState(iframe: HTMLIFrameElement | null): CharterState {
  const cdoc = iframe?.contentDocument;
  if (!cdoc) return readLocal();
  const out: CharterState = {};
  cdoc.querySelectorAll<HTMLTextAreaElement>('textarea.note').forEach((t) => {
    if (t.dataset.key) out[PREFIX + t.dataset.key] = t.value;
  });
  cdoc.querySelectorAll<HTMLInputElement>('.checks input[type=checkbox]').forEach((c) => {
    if (c.dataset.key) out[PREFIX + c.dataset.key] = c.checked ? '1' : '0';
  });
  return out;
}

function readLocal(): CharterState {
  const out: CharterState = {};
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) out[k] = localStorage.getItem(k) ?? '';
    }
  } catch {
    /* storage blocked */
  }
  return out;
}

/** Seed localStorage from the saved copy so the document restores it on init. */
function writeLocal(state: CharterState | null) {
  if (!state) return;
  try {
    Object.entries(state).forEach(([k, v]) => {
      if (k.startsWith(PREFIX) && v != null) localStorage.setItem(k, String(v));
    });
  } catch {
    /* storage blocked */
  }
}

/** Drop every charter key. Restoring an older version must start from empty:
 *  writeLocal only sets the keys a snapshot contains, so anything answered
 *  after that snapshot would otherwise survive and blend into the restored
 *  charter. */
function clearLocal() {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* storage blocked */
  }
}

/** Progress summary for a stored snapshot. No DOM for a version that is not on
 *  screen, so the raw keys are counted instead: "1"/"0" is a checkbox, anything
 *  else is a note. */
function summarize(state: CharterState) {
  const entries = Object.entries(state || {});
  const checks = entries.filter(([, v]) => v === '1' || v === '0');
  const notes = entries.filter(([, v]) => v !== '1' && v !== '0' && String(v).trim());
  return { notes: notes.length, checked: checks.filter(([, v]) => v === '1').length, checks: checks.length };
}

function downloadFile(name: string, content: string, type = 'text/html') {
  const url = URL.createObjectURL(new Blob([content], { type: `${type};charset=utf-8` }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* Re-skin the charter's warm "paper / spruce / blaze" theme onto the CMIT
   palette — deep navy structure and a coral call-to-action on cool slate. This
   is the source app's own override, brought across unchanged: the document was
   authored in cream and forest green, which this design system does not use,
   and the re-skin is how it already looks in EOS.

   Appended AFTER the document's own styles so equal-specificity selectors win
   by source order. The frame is pinned to light because the charter is a
   printed document surface; it does not follow the app's dark mode. */
const palette = `<style id="app-palette">
  :root, :root[data-theme="light"], :root[data-theme="dark"] {
    --paper:#f1f5f9; --paper-2:#f8fafc; --surface:#ffffff;
    --ink:#001f2e; --ink-soft:#475569; --ink-faint:#94a3b8;
    --line:#e2e8f0; --line-strong:#cbd5e1;
    --blaze:#e04e3b; --blaze-ink:#c43d2c;
    --spruce:#1b4b6b; --spruce-deep:#002f44; --gold:#4f82a6;
    --p30:#2e6285; --p60:#4f82a6; --p120:#e76554; --p180:#e04e3b; --summit:#e76554;
    --shadow:0 1px 2px rgba(0,47,68,.06), 0 8px 28px rgba(0,47,68,.08);
    --shadow-lg:0 20px 60px rgba(0,47,68,.16);
  }
  /* Cool the hardcoded warm creams used on the dark navy hero/summit panels. */
  .objective { color:#eaf2f8; }
  .objective p { color:#f6fafd; }
  .objective .who { color:#9db8cc; }
  .objective .eyebrow { color:#f2ada3; }
  .summit p { color:#cbd5e1; }
  .summit ul.ticks li { color:#e2e8f0; }
  .summit .prose[style*="cbbfa6"] { color:#a9c2d6 !important; }
</style>`;

/* The document's script is a same-origin file, not inline: a srcDoc frame
   inherits this app's CSP, and production allows script-src 'self' only. Inline,
   it silently never ran there — no restore, no autosave, and every .reveal
   section left at opacity 0. Placed after the markup, like the original. */
const srcDoc =
  `<!doctype html><html lang="en" data-theme="light"><head><meta charset="utf-8">` +
  `<meta name="viewport" content="width=device-width, initial-scale=1"></head>` +
  `<body>${charterHtml}${palette}<script src="/coo-charter/charter.js"></script></body></html>`;

export function SuccessCharter() {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [ready, setReady] = useState(false); // saved copy loaded -> safe to mount the frame
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [versions, setVersions] = useState<Version[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [frameKey, setFrameKey] = useState(0); // bump to remount the frame after a restore

  /* Seed localStorage from the saved copy BEFORE the frame mounts — the
     document reads storage once, on init, and never looks again. */
  useEffect(() => {
    let alive = true;
    api
      .get<SavedDoc>('/charter/success')
      .then((doc) => {
        if (!alive) return;
        if (doc?.state) writeLocal(doc.state);
        if (doc?.updatedUtc) setSavedAt(doc.updatedUtc);
      })
      .catch(() => {
        /* fall back to whatever is already in this browser */
      })
      .finally(() => {
        if (alive) setReady(true);
      });
    api
      .get<Version[]>('/charter/success/versions')
      .then((v) => {
        if (alive) setVersions(v);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  /** Every save updates the live copy AND appends a restorable snapshot. */
  async function save() {
    setSaving(true);
    setError(null);
    try {
      const state = readCharterState(iframeRef.current);
      const doc = await api.put<{ updatedUtc: string | null }>('/charter/success', { state });
      setSavedAt(doc?.updatedUtc ?? new Date().toISOString());
      try {
        const version = await api.post<Version>('/charter/success/versions', { state });
        setVersions((v) => [version, ...v]);
      } catch {
        /* The charter itself is safely saved; only the history entry failed,
           and saying "could not save" about that would be wrong. */
        setError('Saved, but this version could not be added to the history');
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the charter');
    } finally {
      setSaving(false);
    }
  }

  /** Load an earlier snapshot back into the charter. */
  function restore(v: Version) {
    const when = fmtVersion(v.SavedUtc);
    if (
      !window.confirm(
        `Restore the charter saved ${when}? Anything on the page that you have not saved will be replaced.`,
      )
    )
      return;
    clearLocal();
    writeLocal(v.state);
    setFrameKey((k) => k + 1); // remount so the document re-reads localStorage
    setHistoryOpen(false);
    setError(null);
  }

  async function removeVersion(v: Version) {
    if (
      !window.confirm(
        `Delete the version saved ${fmtVersion(v.SavedUtc)}? The charter itself is not affected.`,
      )
    )
      return;
    await api.del(`/charter/success/versions/${v.VersionId}`);
    setVersions((vs) => vs.filter((x) => x.VersionId !== v.VersionId));
  }

  /** Export what is currently on the page as a standalone, printable deck. */
  function exportReport() {
    const cdoc = iframeRef.current?.contentDocument;
    if (!cdoc) return;
    const html = renderCharterReport(collectCharter(cdoc), {
      savedAt: new Date().toISOString(),
      savedBy: null,
    });
    const d = new Date();
    const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    downloadFile(`coo-charter-${stamp}.html`, html);
  }

  return (
    <div className="csc">
      <div className="csc-bar">
        <span className="csc-hint xs muted">
          Notes and progress autosave to this browser — Save to store them to your workspace.
        </span>
        {error && <span className="xs csc-error">{error}</span>}
        {!error && savedAt && <span className="xs muted csc-saved">Saved {fmtSaved(savedAt)}</span>}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => setHistoryOpen(true)}
          disabled={!ready}
          title={
            versions.length
              ? `${versions.length} saved version${versions.length !== 1 ? 's' : ''}`
              : 'No saved versions yet'
          }
        >
          History{versions.length ? ` (${versions.length})` : ''}
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={exportReport}
          disabled={!ready}
          title="Download the charter as a printable report"
        >
          Export report
        </button>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => void save()}
          disabled={saving || !ready}
          title="Save your charter notes and progress to the workspace"
        >
          {saving ? 'Saving…' : 'Save charter'}
        </button>
      </div>

      {ready && (
        <iframe
          key={frameKey}
          ref={iframeRef}
          title="COO Success Charter"
          srcDoc={srcDoc}
          className="csc-frame"
        />
      )}

      {historyOpen && (
        <Modal title="Charter history" onClose={() => setHistoryOpen(false)}>
          {versions.length === 0 ? (
            <p className="muted small">
              No saved versions yet. Each time you hit <strong>Save charter</strong>, a snapshot is kept here
              so you can come back to it.
            </p>
          ) : (
            <div className="csc-versions">
              {versions.map((v) => {
                const s = summarize(v.state);
                return (
                  <div key={v.VersionId} className="csc-version">
                    <div className="grow">
                      <div className="small strong">{fmtVersion(v.SavedUtc)}</div>
                      <div className="xs muted">
                        {s.notes} note{s.notes !== 1 ? 's' : ''} · {s.checked}/{s.checks} deliverables
                        {v.SavedBy ? ` · ${v.SavedBy}` : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      title="Restore this version into the charter"
                      onClick={() => restore(v)}
                    >
                      Restore
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm csc-danger"
                      title="Delete this version"
                      onClick={() => void removeVersion(v)}
                    >
                      Delete
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}
